// Channel Read State - NGXS State Management
import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { ChannelRead, PaginationInfo } from '../../models/chat.models';
import { ChatService } from '../../services/chat.service';

// Import individual actions using ES2015 syntax
import {
    LoadChannelReads,
    LoadChannelReadsSuccess,
    LoadChannelReadsError,
    UpdateChannelRead,
    MarkChannelAsRead,
    IncrementUnreadCount,
    DecrementUnreadCount,
    ResetUnreadCount,
    ClearChannelReads,
} from '../actions/channel-read.actions';

export interface ChannelReadStateModel {
    channelReads: { [key: string]: ChannelRead }; // key: `${channelId}_${userId}`
    loading: boolean;
    error: any;
    pagination: PaginationInfo;
    lastUpdated: number;
    // Stored during auto-pagination so success handler can fetch subsequent pages
    // without requiring every caller to implement its own pagination loop.
    syncUserId?: string;
    syncLimit?: number;
}

@State<ChannelReadStateModel>({
    name: 'channelReads',
    defaults: {
        channelReads: {},
        loading: false,
        error: null,
        pagination: { hasMore: false },
        lastUpdated: 0,
    },
})
@Injectable()
export class ChannelReadState {
    constructor(private chatService: ChatService) {}

    private static getKey(channelId: string, userId: string): string {
        return `${channelId}_${userId}`;
    }

    @Selector()
    static getChannelReads(state: ChannelReadStateModel): ChannelRead[] {
        return Object.values(state.channelReads);
    }

    @Selector()
    static getChannelRead(state: ChannelReadStateModel) {
        return (channelId: string, userId: string): ChannelRead | undefined => {
            const key = ChannelReadState.getKey(channelId, userId);
            return state.channelReads[key];
        };
    }

    @Selector()
    static getUnreadCount(state: ChannelReadStateModel) {
        return (channelId: string, userId: string): number => {
            const key = ChannelReadState.getKey(channelId, userId);
            const channelRead = state.channelReads[key];
            return channelRead?.unreadCount || 0;
        };
    }

    @Selector()
    static getChannelReadsByUserId(state: ChannelReadStateModel) {
        return (userId: string): ChannelRead[] => {
            return Object.values(state.channelReads).filter((cr) => cr.userId === userId);
        };
    }

    @Selector()
    static getTotalUnreadCount(state: ChannelReadStateModel) {
        return (userId: string): number => {
            return Object.values(state.channelReads)
                .filter((cr) => cr.userId === userId)
                .reduce((total, cr) => total + cr.unreadCount, 0);
        };
    }

    @Selector()
    static getUnreadChannelIds(state: ChannelReadStateModel) {
        return (userId: string): string[] => {
            return Object.values(state.channelReads)
                .filter((cr) => cr.userId === userId && cr.unreadCount > 0)
                .map((cr) => cr.channelId);
        };
    }

    @Selector()
    static isLoading(state: ChannelReadStateModel): boolean {
        return state.loading;
    }

    @Selector()
    static getError(state: ChannelReadStateModel): any {
        return state.error;
    }

    @Selector()
    static getPagination(state: ChannelReadStateModel): PaginationInfo {
        return state.pagination;
    }

    @Action(LoadChannelReads)
    loadChannelReads(ctx: StateContext<ChannelReadStateModel>, action: LoadChannelReads) {
        if (!action.nextToken) {
            // Fresh load: wipe stale data so deleted DynamoDB records don't linger,
            // and record the userId/limit so the success handler can auto-paginate
            // without requiring every caller to implement its own pagination loop.
            ctx.patchState({
                channelReads: {},
                loading: true,
                error: null,
                syncUserId: action.userId,
                syncLimit: action.limit,
            });
        } else {
            ctx.patchState({ loading: true, error: null });
        }

        return this.chatService
            .getChannelReadsByUserId(action.userId, action.limit, action.nextToken)
            .pipe(
                tap((result) => {
                    ctx.dispatch(new LoadChannelReadsSuccess(result.items, result.nextToken));
                }),
                catchError((error) => {
                    ctx.dispatch(new LoadChannelReadsError(error));
                    return of(error);
                }),
            );
    }

    @Action(LoadChannelReadsSuccess)
    loadChannelReadsSuccess(
        ctx: StateContext<ChannelReadStateModel>,
        action: LoadChannelReadsSuccess,
    ) {
        const state = ctx.getState();
        const newChannelReads = { ...state.channelReads };

        // Add or update channel reads (merge handles pagination append)
        action.channelReads.forEach((cr) => {
            const key = ChannelReadState.getKey(cr.channelId, cr.userId);
            newChannelReads[key] = cr;
        });

        const hasMore = !!action.nextToken;

        ctx.patchState({
            channelReads: newChannelReads,
            // Keep loading=true while more pages are coming so consumers can react
            // to a single false transition when all reads have been fetched.
            loading: hasMore,
            error: null,
            pagination: {
                nextToken: action.nextToken,
                hasMore,
            },
            lastUpdated: Date.now(),
        });

        // Auto-paginate: if the backend returned a nextToken, transparently fetch
        // the next page using the userId/limit recorded on the initial fresh load.
        if (hasMore && state.syncUserId) {
            ctx.dispatch(new LoadChannelReads(state.syncUserId, state.syncLimit, action.nextToken));
        }
    }

    @Action(LoadChannelReadsError)
    loadChannelReadsError(ctx: StateContext<ChannelReadStateModel>, action: LoadChannelReadsError) {
        ctx.patchState({
            loading: false,
            error: action.error,
        });
    }

    @Action(UpdateChannelRead)
    updateChannelRead(ctx: StateContext<ChannelReadStateModel>, action: UpdateChannelRead) {
        const state = ctx.getState();
        const key = ChannelReadState.getKey(
            action.channelRead.channelId,
            action.channelRead.userId,
        );

        ctx.patchState({
            channelReads: {
                ...state.channelReads,
                [key]: action.channelRead,
            },
            lastUpdated: Date.now(),
        });
    }

    @Action(MarkChannelAsRead)
    markChannelAsRead(ctx: StateContext<ChannelReadStateModel>, action: MarkChannelAsRead) {
        const state = ctx.getState();
        const key = ChannelReadState.getKey(action.channelId, action.userId);
        const existing = state.channelReads[key];

        // Optimistic: clear badge immediately before the API round-trip.
        // Use a fresh client-side timestamp so that any delayed subscription
        // increment events triggered by messages BEFORE this read are
        // correctly identified as stale and dropped by the timestamp guard
        // in subscribeToUnreadCountChange.
        ctx.patchState({
            channelReads: {
                ...state.channelReads,
                [key]: {
                    channelId: action.channelId,
                    userId: action.userId,
                    lastReadMessageId: action.lastReadMessageId,
                    unreadCount: 0,
                    updatedAt: new Date().toISOString(),
                },
            },
        });

        return this.chatService
            .markChannelAsRead(action.channelId, action.userId, action.lastReadMessageId)
            .pipe(
                tap((channelRead) => {
                    ctx.dispatch(new UpdateChannelRead(channelRead));
                }),
                catchError((error) => {
                    if (existing) {
                        ctx.patchState({
                            channelReads: { ...ctx.getState().channelReads, [key]: existing },
                        });
                    }
                    ctx.patchState({ error });
                    return of(error);
                }),
            );
    }

    @Action(IncrementUnreadCount)
    incrementUnreadCount(ctx: StateContext<ChannelReadStateModel>, action: IncrementUnreadCount) {
        return this.chatService
            .incrementUnreadCount(action.channelId, action.userId, action.increment)
            .pipe(
                tap((channelRead) => {
                    ctx.dispatch(new UpdateChannelRead(channelRead));
                }),
                catchError((error) => {
                    ctx.patchState({ error });
                    return of(error);
                }),
            );
    }

    @Action(DecrementUnreadCount)
    decrementUnreadCount(ctx: StateContext<ChannelReadStateModel>, action: DecrementUnreadCount) {
        return this.chatService
            .decrementUnreadCount(action.channelId, action.userId, action.decrement)
            .pipe(
                tap((channelRead) => {
                    ctx.dispatch(new UpdateChannelRead(channelRead));
                }),
                catchError((error) => {
                    ctx.patchState({ error });
                    return of(error);
                }),
            );
    }

    @Action(ResetUnreadCount)
    resetUnreadCount(ctx: StateContext<ChannelReadStateModel>, action: ResetUnreadCount) {
        return this.chatService.resetUnreadCount(action.channelId, action.userId).pipe(
            tap((channelRead) => {
                ctx.dispatch(new UpdateChannelRead(channelRead));
            }),
            catchError((error) => {
                ctx.patchState({ error });
                return of(error);
            }),
        );
    }

    @Action(ClearChannelReads)
    clearChannelReads(ctx: StateContext<ChannelReadStateModel>) {
        ctx.patchState({
            channelReads: {},
            loading: false,
            error: null,
            pagination: { hasMore: false },
            lastUpdated: 0,
        });
    }
}
